import { serverNow } from "./serverClock";

/** RN/tsconfig setups often ship incomplete `URLSearchParams` typings. */
function searchParamsGet(params: URLSearchParams, name: string): string | null {
  return (params as unknown as { get(n: string): string | null }).get(name);
}

function searchParamsSet(
  params: URLSearchParams,
  name: string,
  value: string,
): void {
  (params as unknown as { set(n: string, v: string): void }).set(name, value);
}

type SectionLike = {
  sub_sections: { groups: unknown[] }[];
};

export type NavigationMode = "none" | "subjects" | "subsections" | "groups";

export function getNavigationMode(sections: SectionLike[]): NavigationMode {
  if (sections.length === 0) return "none";
  if (sections.length > 1) return "subjects";
  const subSections = sections[0]!.sub_sections;
  if (subSections.length > 1) return "subsections";
  if (subSections.length === 0) return "none";
  if (subSections[0]!.groups.length > 1) return "groups";
  return "none";
}

export const SUBJECT_ICONS: Record<string, string> = {
  // Standard subjects
  physics: "physicsMain2Icon",
  chemistry: "chemistryMainIcon",
  mathematics: "mathsMainIcon",
  maths: "mathsMainIcon",
  math: "mathsMainIcon",
  botany: "botanyMainIcon",
  zoology: "zoologyMainIcon",
  biology: "biologyMainIcon",
  english: "englishMainIcon",
  civics: "civicsMainIcon",
  commerce: "commerceMainIcon",
  computer: "computerMainIcon",
  "computer science": "computerMainIcon",
  "critical thinking": "criticalThinkingMainIcon",
  economics: "economicsMainIcon",
  evs: "evsMainIcon",
  geography: "geographyMainIcon",
  "general knowledge": "gkMainIcon",
  gk: "gkMainIcon",
  hindi: "hindiMainIcon",
  history: "historyMainIcon",
  "logical reasoning": "logicalMainIcon",
  logical: "logicalMainIcon",
  "problem solving": "problemSolvingMainIcon",
  science: "scienceMainIcon",
  social: "socialMainIcon",
  "social science": "socialMainIcon",
  "social studies": "socialMainIcon",
  statistics: "statsMainIcon",
  stats: "statsMainIcon",
  "vedic math": "vedicMatchMainIcon",
  "vedic maths": "vedicMatchMainIcon",
  business: "businessMainIcon",
  "business studies": "businessMainIcon",
  phy: "physicsMain2Icon",
  chem: "chemistryMainIcon",
  bio: "biologyMainIcon",
  mat: "mathsMainIcon",
  "english v1": "englishMainIcon",
  "general test": "gkMainIcon",
  "math/applied math": "mathsMainIcon",
  "applied mathematics": "mathsMainIcon",
  "mental ability": "criticalThinkingMainIcon",
  arithmetic: "mathsMainIcon",
  "english enhancement program": "englishMainIcon",
  "environmental studies": "evsMainIcon",
  reasoning: "logicalMainIcon",
  // Use generic icon for these
  telugu: "genericMainIcon",
  "now live": "genericMainIcon",
  "starts today": "genericMainIcon",
  "starts tomorrow": "genericMainIcon",
  nasa: "genericMainIcon",
  kat: "genericMainIcon",
  hots: "genericMainIcon",
  intso: "genericMainIcon",
};

export const getIconName = (title: string): string =>
  SUBJECT_ICONS[title.trim().toLowerCase()] ?? "genericMainIcon";

export type QuestionNavStatus =
  | "notVisited"
  | "notAnswered"
  | "answered"
  | "markedForReview"
  | "answeredMarkedForReview";

export function getQuestionStatus(
  globalIndex: number,
  answered: Set<number>,
  reviewed: Set<number>,
  visited: Set<number>,
  isPenPaper = false,
): QuestionNavStatus {
  const isReviewed = reviewed.has(globalIndex);
  if (isPenPaper) {
    // Pen & paper: only 3 states — visited (green), not visited, marked for review
    if (isReviewed) return "markedForReview";
    if (visited.has(globalIndex)) return "answered"; // visited shows as green (answered color)
    return "notVisited";
  }
  const isAnswered = answered.has(globalIndex);
  if (isAnswered && isReviewed) return "answeredMarkedForReview";
  if (isReviewed) return "markedForReview";
  if (isAnswered) return "answered";
  if (visited.has(globalIndex)) return "notAnswered";
  return "notVisited";
}

export type StatusCounts = Record<QuestionNavStatus, number>;

export function getStatusCounts(
  questions: { globalIndex: number }[],
  answered: Set<number>,
  reviewed: Set<number>,
  visited: Set<number>,
  isPenPaper = false,
): StatusCounts {
  const counts: StatusCounts = {
    notVisited: 0,
    notAnswered: 0,
    answered: 0,
    markedForReview: 0,
    answeredMarkedForReview: 0,
  };
  for (const q of questions) {
    counts[
      getQuestionStatus(q.globalIndex, answered, reviewed, visited, isPenPaper)
    ]++;
  }
  return counts;
}

export function stripHtml(html: string): string {
  return html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();
}

export function formatDateTime(dateStr: string): string {
  const date = new Date(dateStr);
  const day = date.getDate();
  const month = date.toLocaleString("en-US", { month: "short" });
  const time = date
    .toLocaleString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .toUpperCase();
  return `${day} ${month} ${time}`;
}
export function formatChallengeDate(dateStr: string): string {
  const date = new Date(dateStr);
  const day = date.getDate();
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();
  return `${day} ${month}, ${year}`;
}

export function formatTime(totalSeconds: number): string {
  const hrs = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  return `${String(hrs).padStart(2, "0")}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

export function formatTimeLabeled(totalSeconds: number): string {
  const days = Math.floor(totalSeconds / 86400);
  const hrs = Math.floor((totalSeconds % 86400) / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  if (days > 0) {
    return `${days}d:${hrs}h:${String(mins).padStart(2, "0")}m:${String(secs).padStart(2, "0")}s`;
  }
  if (hrs > 0) {
    return `${hrs}h:${String(mins).padStart(2, "0")}m:${String(secs).padStart(2, "0")}s`;
  }
  return `${String(mins).padStart(2, "0")}m:${String(secs).padStart(2, "0")}s`;
}

export function toISOWithOffset(input: Date | string): string {
  const iso = input instanceof Date ? input.toISOString() : input;
  return iso.endsWith("Z") ? iso.slice(0, -1) + "+00:00" : iso;
}

/** A SCORE test is considered "live" from this many ms before its start time
 * through to its end time. */
export const SCORE_LIVE_THRESHOLD_MS = 10 * 60 * 1000;

export function isScoreTestLive(
  startDate?: string | null,
  endDate?: string | null,
): boolean {
  if (!startDate || !endDate) return false;
  try {
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    if (!Number.isFinite(start) || !Number.isFinite(end)) return false;
    const now = serverNow();
    return now >= start - SCORE_LIVE_THRESHOLD_MS && now <= end;
  } catch {
    return false;
  }
}

export function getScoreTestTimerText(
  startDate: string,
  now: number,
): string | null {
  if (!startDate) return null;
  const start = new Date(startDate).getTime();
  const diff = start - now;

  if (diff <= SCORE_LIVE_THRESHOLD_MS) return null;

  const totalSeconds = Math.max(0, Math.floor(diff / 1000));
  const days = Math.floor(totalSeconds / (60 * 60 * 24));
  const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60));
  const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
  const seconds = totalSeconds % 60;

  const hh = String(hours).padStart(2, "0");
  const mm = String(minutes).padStart(2, "0");
  const ss = String(seconds).padStart(2, "0");

  if (days > 0) {
    return `Starts in ${days}d : ${hh}h : ${mm}m`;
  }
  return `Starts in ${hh}h : ${mm}m : ${ss}s`;
}

export interface ScoreTimerParts {
  days: number | null;
  hh: string;
  mm: string;
  ss: string;
}

export function getScoreTestTimerParts(
  startDate: string,
  now: number,
): ScoreTimerParts | null {
  if (!startDate) return null;
  const start = new Date(startDate).getTime();
  const diff = start - now;

  if (diff <= SCORE_LIVE_THRESHOLD_MS) return null;

  const totalSeconds = Math.max(0, Math.floor(diff / 1000));
  const days = Math.floor(totalSeconds / (60 * 60 * 24));
  const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60));
  const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
  const seconds = totalSeconds % 60;

  return {
    days: days > 0 ? days : null,
    hh: String(hours).padStart(2, "0"),
    mm: String(minutes).padStart(2, "0"),
    ss: String(seconds).padStart(2, "0"),
  };
}

export const CYOT_DURATION_OPTIONS = [15, 20, 25, 30] as const;
export const CYOT_QUESTION_OPTIONS = [10, 15, 20, 25] as const;

type ProgramLike = {
  isDefault?: boolean;
  gradeId?: number;
  examName?: string;
  examId?: number;
};

type ProgramContainer<TProgram extends ProgramLike> = {
  enrolledPrograms?: TProgram[] | null;
  selectedProgram?: TProgram | null;
};

export const getDefaultProgram = <TProgram extends ProgramLike>(
  dataDto: ProgramContainer<TProgram>,
): TProgram | undefined =>
  dataDto.selectedProgram ??
  dataDto.enrolledPrograms?.find((program) => program.isDefault) ??
  dataDto.enrolledPrograms?.[0];

export const createShareUrl = (
  test: { id: string },
  dataDto: ProgramContainer<ProgramLike>,
  studentWebUrl: string,
) => {
  const defaultProgram = getDefaultProgram(dataDto);
  return buildCyotTestLink({
    paperId: test.id,
    grade: defaultProgram?.gradeId,
    examName: defaultProgram?.examName,
    exam_type: defaultProgram?.examId
      ? String(defaultProgram.examId)
      : undefined,
    studentWebUrl,
  });
};

type BrowserGlobal = {
  btoa?: (data: string) => string;
  atob?: (data: string) => string;
  location?: { origin: string };
};

const g = globalThis as unknown as BrowserGlobal;

const encodeCyotId = (paperId: string) => {
  if (typeof g.btoa === "function") {
    return g.btoa(paperId);
  }
  return paperId;
};

export const decodeCyotId = (encodedPaperId: string) => {
  if (typeof g.atob === "function") {
    try {
      return g.atob(encodedPaperId);
    } catch {
      return encodedPaperId;
    }
  }
  return encodedPaperId;
};

export const buildCyotTestLink = ({
  paperId,
  grade,
  examName,
  exam_type,
  studentWebUrl,
}: {
  paperId?: string;
  grade?: number;
  examName?: string;
  exam_type?: string;
  studentWebUrl: string;
}): string => {
  if (!paperId || !grade || !exam_type || !studentWebUrl) return "";

  const params = new URLSearchParams();
  params.append("module", "tests");
  params.append("grade", String(grade));
  params.append("isFromDeepLinking", "true");
  params.append("cyotID", encodeCyotId(paperId));
  if (examName) params.append("examname", examName);
  params.append("examtype", exam_type);

  return `${studentWebUrl}/dashboard?${params.toString()}`;
};

export interface SharedTestParams {
  paperId: string;
  grade: number;
  examName?: string;
  examType: string;
}

export const parseSharedTestParams = (
  searchParams: URLSearchParams,
): SharedTestParams | null => {
  const module = searchParamsGet(searchParams, "module");
  const isFromDeepLinking = searchParamsGet(searchParams, "isFromDeepLinking");
  const cyotID = searchParamsGet(searchParams, "cyotID");
  const grade = searchParamsGet(searchParams, "grade");
  const examName = searchParamsGet(searchParams, "examname");
  const examType = searchParamsGet(searchParams, "examtype");

  if (
    module !== "tests" ||
    isFromDeepLinking !== "true" ||
    !cyotID ||
    !grade ||
    !examType
  ) {
    return null;
  }

  const paperId = decodeCyotId(cyotID);
  if (!paperId) return null;

  return {
    paperId,
    grade: parseInt(grade, 10),
    examName: examName || undefined,
    examType,
  };
};

const TWO_HOURS_MS = 2 * 60 * 60 * 1000;

export interface BuildReportUrlOptions {
  examId?: string;
  assessmentId?: string;
  questionPaperId?: string;
  src: string;
  studentEndTime?: string | null;
  startTime?: string | null;
  detailReportAvailableAfter?: string | null;
  fromViewReport?: boolean;
  examMode?: string | null;
  title?: string;
  challengeWindowEndsAt?: string | null;
}

export interface ReportNavParams {
  examId?: string;
  examSlotExamId?: string;
  assessmentId?: string;
  questionPaperId?: string;
  src: string;
  examMode?: string;
  reportType?: "basic" | "detailed";
  startTime?: string | null; // exam start time or student start time for lsq event
  studentEndTime?: string;
  detailReportAvailableAfter?: string;
  fromViewReport?: boolean;
  title?: string;
  challengeWindowEndsAt?: string | null;
}

export function buildReportNavParams({
  examId,
  assessmentId,
  questionPaperId,
  src,
  startTime,
  studentEndTime,
  detailReportAvailableAfter,
  fromViewReport,
  examMode,
  title,
  challengeWindowEndsAt,
}: BuildReportUrlOptions): ReportNavParams {
  const params: ReportNavParams = { src };

  if (examId) params.examId = examId;
  if (questionPaperId) params.questionPaperId = questionPaperId;
  if (examMode) params.examMode = examMode;
  if (title) params.title = title;
  if (challengeWindowEndsAt)
    params.challengeWindowEndsAt = challengeWindowEndsAt;

  const isBasic = isWithinGateWindow(
    studentEndTime,
    detailReportAvailableAfter,
  );

  if (isBasic) {
    if (examId) params.examSlotExamId = examId;
    params.reportType = "basic";
    if (studentEndTime) params.studentEndTime = studentEndTime;
    if (detailReportAvailableAfter) {
      params.detailReportAvailableAfter = detailReportAvailableAfter;
    }
  } else if (assessmentId) {
    params.assessmentId = assessmentId;
  }

  if (fromViewReport) params.fromViewReport = true;

  if (startTime) params.startTime = startTime;

  return params;
}

export function buildReportUrl(options: BuildReportUrlOptions): string {
  const params = buildReportNavParams(options);
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value == null || value === false) continue;
    const serialized = value === true ? "true" : String(value);
    const urlKey = key === "examSlotExamId" ? "examSlotId" : key;
    searchParamsSet(search, urlKey, serialized);
  }
  return `/reports?${search.toString()}`;
}

function isWithinGateWindow(
  studentEndTime?: string | null,
  detailReportAvailableAfter?: string | null,
): boolean {
  const nowMs = serverNow();

  if (detailReportAvailableAfter) {
    const gateMs = new Date(detailReportAvailableAfter).getTime();
    if (!Number.isNaN(gateMs) && nowMs < gateMs) return true;
  }

  if (studentEndTime) {
    const endMs = new Date(studentEndTime).getTime();
    if (!Number.isNaN(endMs) && endMs > 0 && nowMs < endMs + TWO_HOURS_MS)
      return true;
  }

  return false;
}

/**
 * Backend `shortname` values that don't directly match a route/screen key.
 * Keys are normalized shortnames (uppercased, whitespace → underscore).
 */
export const TEST_SHORTNAME_ALIASES: Record<string, string> = {
  TEST_SERIES: "ILTS",
};

/** Normalize a backend `shortname` to a route/screen key style. */
export const normalizeTestShortname = (shortname: string) =>
  shortname.toUpperCase().replace(/\s+/g, "_");

/**
 * Resolve a test card's `shortname` to a route/screen key, applying aliases.
 * Returned key may not exist in a given platform's route table — caller must
 * check before navigating.
 */
export const resolveTestRouteKey = (shortname: string) => {
  const normalized = normalizeTestShortname(shortname);
  return TEST_SHORTNAME_ALIASES[normalized] ?? normalized;
};
